// utils/scriptFolderName.js
//
// Single source of truth for an automation script's folder name and S3 path.
//
// The folder is created by one code path and the `projectPath` inside the script's
// own request_payload.json is written by another. When those two derived the name
// independently they drifted — the folder was uppercased and the payload was not —
// so every upload the editor addressed via `projectPath` landed on a prefix that
// differed from the real folder by casing, and S3 surfaced it as a second script
// (TSB-4519). The dedup suffix drifted the same way: `_1` was appended to the
// folder only, pointing the payload at a *different* script's folder.
//
// Both consumers now resolve the name here, so they cannot disagree.

/** Characters S3/Windows cannot carry in a path segment. */
const INVALID_PATH_CHARS = /[<>:"/\\|?*]/g;

/** The fixed subfolder every automation script lives under. */
export const AUTOMATION_FOLDER = 'Automation Scripts';

/**
 * Normalise a name into a folder segment: drop invalid characters, collapse
 * whitespace into underscores, uppercase.
 *
 * Uppercasing is what makes the folder name stable: it is the one transform the
 * generated test name and a user-typed override have in common, so a script keeps
 * the same folder whether or not it was renamed during generation.
 */
export const toScriptFolderSegment = (name) =>
  String(name || '')
    .replace(INVALID_PATH_CHARS, '')
    .replace(/\s+/g, '_')
    .trim()
    .toUpperCase();

/**
 * Derive a folder name from the generated config, falling back to the endpoint.
 *
 * @param {Object} endpoint        - { method, path }
 * @param {Object|null} testConfigData - Parsed request_payload.json, if available
 * @returns {string} Folder segment (uppercase)
 */
export const deriveScriptFolderName = (endpoint, testConfigData = null) => {
  const testName = testConfigData?.tests?.[0]?.name;
  if (testName) return toScriptFolderSegment(testName);

  const pathPart = String(endpoint?.path || '').replace(/[/{}]/g, '_').replace(/^_|_$/g, '');
  const method = String(endpoint?.method || 'GET').toUpperCase();
  return toScriptFolderSegment(`${method}_${pathPart}`.replace(/__+/g, '_'));
};

/**
 * Resolve the folder name for one endpoint, honouring a user-typed override.
 * The override wins but is normalised the same way, so it cannot reintroduce drift.
 */
export const resolveScriptFolderName = ({ endpoint, testConfigData = null, customName = '' }) =>
  (customName ? toScriptFolderSegment(customName) : deriveScriptFolderName(endpoint, testConfigData));

/**
 * Append `_1`, `_2`, … until the name is unused.
 *
 * @param {string} folderName
 * @param {Set<string>} usedLowerCaseNames - Names already taken, lowercased.
 *   S3 keys are case-sensitive but two folders differing only by case are
 *   indistinguishable to a reader, so collisions are compared case-insensitively.
 * @returns {string} A name not present in the set
 */
export const dedupeScriptFolderName = (folderName, usedLowerCaseNames) => {
  if (!usedLowerCaseNames?.has(folderName.toLowerCase())) return folderName;
  let suffix = 1;
  while (usedLowerCaseNames.has(`${folderName}_${suffix}`.toLowerCase())) suffix++;
  return `${folderName}_${suffix}`;
};

/** Absolute S3 path of a script folder. Must match where the folder is created. */
export const buildScriptProjectPath = (projectId, projectName, folderName) =>
  `projects/${projectId}/${projectName}/${AUTOMATION_FOLDER}/${folderName}`;

/**
 * Resolve the final folder name for every generated config in one pass, so the
 * payload and the folder that gets created are guaranteed to agree.
 *
 * @param {Array}  testConfigs        - [{ endpoint, files }]
 * @param {Object} customFolderNames  - Overrides keyed by `${method}-${path}`
 * @param {Array}  existingNames      - Folder names already in Automation Scripts
 * @param {Function} parseConfig      - testConfig -> parsed request_payload.json | null
 * @returns {Map<string, string>} endpointKey -> final folder name
 */
export const resolveScriptFolderNames = (testConfigs, customFolderNames, existingNames, parseConfig) => {
  const used = new Set((existingNames || []).map(n => String(n).toLowerCase()));
  const names = new Map();

  for (const testConfig of testConfigs || []) {
    const endpointKey = `${testConfig.endpoint.method}-${testConfig.endpoint.path}`;
    const resolved = resolveScriptFolderName({
      endpoint: testConfig.endpoint,
      testConfigData: parseConfig(testConfig),
      customName: customFolderNames?.[endpointKey] || '',
    });
    const finalName = dedupeScriptFolderName(resolved, used);
    used.add(finalName.toLowerCase());
    names.set(endpointKey, finalName);
  }

  return names;
};
